import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// ============ Types ============

export interface DataSubjectRequest {
  id: string;
  user_id: string;
  request_type: 'export' | 'deletion' | 'access' | 'rectification';
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  requested_at: string;
  processed_at: string | null;
  processed_by: string | null;
  download_url: string | null;
  download_expires_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExportedData {
  exported_at: string;
  user_id: string;
  profile: Record<string, unknown> | null;
  progress_logs: Array<Record<string, unknown>>;
  cravings: Array<Record<string, unknown>>;
  assessment_responses: Array<Record<string, unknown>>;
  consents: Array<Record<string, unknown>>;
  bookings: Array<Record<string, unknown>>;
}

// ============ Query Keys ============

export const dataSubjectKeys = {
  all: ['data-subject'] as const,
  requests: (userId: string) => [...dataSubjectKeys.all, 'requests', userId] as const,
  request: (requestId: string) => [...dataSubjectKeys.all, 'request', requestId] as const,
};

// ============ Hooks ============

/**
 * Get all data subject requests for a user
 */
export function useDataSubjectRequests(userId: string | undefined) {
  return useQuery({
    queryKey: dataSubjectKeys.requests(userId || ''),
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('data_subject_requests')
        .select('*')
        .eq('user_id', userId)
        .order('requested_at', { ascending: false });
      
      if (error) throw error;
      return data as DataSubjectRequest[];
    },
    enabled: !!userId,
  });
}

/**
 * Get a specific data subject request
 */
export function useDataSubjectRequest(requestId: string | undefined) {
  return useQuery({
    queryKey: dataSubjectKeys.request(requestId || ''),
    queryFn: async () => {
      if (!requestId) return null;
      
      const { data, error } = await supabase
        .from('data_subject_requests')
        .select('*')
        .eq('id', requestId)
        .single();
      
      if (error) throw error;
      return data as DataSubjectRequest;
    },
    enabled: !!requestId,
  });
}

/**
 * Create a new data subject request
 */
export function useCreateDataSubjectRequest() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({
      userId,
      requestType,
      notes,
    }: {
      userId: string;
      requestType: DataSubjectRequest['request_type'];
      notes?: string;
    }) => {
      const { data, error } = await supabase
        .from('data_subject_requests')
        .insert({
          user_id: userId,
          request_type: requestType,
          notes,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as DataSubjectRequest;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataSubjectKeys.requests(variables.userId) });
      toast.success(t('privacy.requestSubmitted', 'Your request has been submitted'));
    },
    onError: (error: any) => {
      console.error('Failed to create data subject request:', error);
      
      // Handle duplicate pending request
      if (error.message?.includes('duplicate') || error.code === '23505') {
        toast.error(t('privacy.duplicateRequest', 'You already have a pending request of this type'));
      } else {
        toast.error(t('privacy.requestError', 'Failed to submit request'));
      }
    },
  });
}

/**
 * Cancel a pending data subject request
 */
export function useCancelDataSubjectRequest() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({
      requestId,
      userId,
    }: {
      requestId: string;
      userId: string;
    }) => {
      const { data, error } = await supabase
        .from('data_subject_requests')
        .update({ status: 'cancelled' })
        .eq('id', requestId)
        .eq('user_id', userId)
        .eq('status', 'pending')
        .select()
        .single();
      
      if (error) throw error;
      return data as DataSubjectRequest;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: dataSubjectKeys.requests(variables.userId) });
      toast.success(t('privacy.requestCancelled', 'Request cancelled'));
    },
    onError: (error) => {
      console.error('Failed to cancel request:', error);
      toast.error(t('privacy.cancelError', 'Failed to cancel request'));
    },
  });
}

/**
 * Export user's PHI data (calls the database function)
 */
export function useExportUserData() {
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.rpc('export_user_phi_data', {
        p_user_id: userId,
      });
      
      if (error) throw error;
      return data as unknown as ExportedData;
    },
    onSuccess: () => {
      toast.success(t('privacy.exportReady', 'Your data export is ready'));
    },
    onError: (error) => {
      console.error('Failed to export user data:', error);
      toast.error(t('privacy.exportError', 'Failed to export data'));
    },
  });
}

/**
 * Download exported data as JSON file
 */
export function downloadDataAsFile(data: ExportedData, filename?: string) {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `my-data-export-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}
